/*
 * Tournament detail — the event command center. Web port of
 * apps/mobile/src/screens/tournaments/TournamentDetailScreen.tsx.
 *
 * For W1.2 this lands the scoreboard + overview + owner action row.
 * Roster, Teams, and Activity tabs come in W1.3 alongside the
 * AddPlayer / TeamSetup screens. All API state is loaded fresh on mount
 * so a publish-then-back-then-forward dance shows the right numbers.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Plus, Users, Pencil, AlertTriangle, Flag, Trophy, Shuffle, Newspaper,
  Send, CheckCircle2, Star, Copy, MessageSquare, Check,
} from 'lucide-react';

import { api } from '@/lib/api';
import { useTournamentStore } from '@/store/tournamentStore';
import { AppShell } from '@/components/AppShell';
import { ConfirmModal } from '@/components/ConfirmModal';
import { EventLogo } from '@/components/EventLogo';
import { getInviteStatus, inviteStatusLabel, inviteStatusColor } from '@/lib/inviteStatus';
import { formatTeamCupPoints } from '@/lib/teamCupPoints';
import { MyMatchesSection } from '@/routes/tournaments/MyMatchesSection';
import type { EventPlayer } from '@golf-app/shared';

/** Tiny colored dot rendered after a player's name on the event-detail
 *  team rosters. Green = joined, gold = invite pending, grey = not
 *  invited. Tooltip carries the label so the visible row stays compact. */
function StatusDot({ player }: { player: EventPlayer }) {
  const s = getInviteStatus(player);
  const c = inviteStatusColor(s);
  return (
    <span
      title={inviteStatusLabel(s)}
      aria-label={inviteStatusLabel(s)}
      style={{
        display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
        background: c, flexShrink: 0,
      }}
    />
  );
}
import {
  StatusPill, formatDateRange, winningRuleLabel, visibilityLabel,
} from './_shared';

export default function TournamentDetail() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { activeEvent, setActiveEvent } = useTournamentStore();

  const [event, setEvent] = useState(activeEvent?.id === eventId ? activeEvent : null);
  const [loading, setLoading] = useState(!event);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEvent = useCallback(async () => {
    if (!eventId) return;
    setError(null);
    try {
      const ev = await api.getTournament(eventId);
      setEvent(ev);
      setActiveEvent(ev);
    } catch (e: any) {
      setError(e?.message ?? "Couldn't load tournament");
    } finally {
      setLoading(false);
    }
  }, [eventId, setActiveEvent]);

  useEffect(() => { fetchEvent(); }, [fetchEvent]);

  async function patchStatus(next: 'active' | 'completed') {
    if (!eventId || !event) return;
    setBusy(true);
    try {
      const updated = await api.updateTournament(eventId, { status: next });
      setEvent(updated);
      setActiveEvent(updated);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to update event');
    } finally {
      setBusy(false);
    }
  }

  // The ConfirmModal is single-purpose. We store which intent is currently
  // pending so the same modal renders the right copy + behavior.
  type ConfirmIntent = null | 'archive' | 'delete';
  const [pendingConfirm, setPendingConfirm] = useState<ConfirmIntent>(null);

  async function doArchive() {
    if (!eventId || !event) return;
    setBusy(true);
    try {
      await api.archiveTournament(eventId);
      navigate('/app/tournaments', { replace: true });
    } catch (e: any) {
      setError(e?.message ?? 'Failed to archive');
      setBusy(false);
      setPendingConfirm(null);
    }
  }

  async function doDelete() {
    if (!eventId || !event) return;
    setBusy(true);
    try {
      await api.deleteTournament(eventId);
      navigate('/app/tournaments', { replace: true });
    } catch (e: any) {
      setError(e?.message ?? 'Failed to delete');
      setBusy(false);
      setPendingConfirm(null);
    }
  }

  if (loading) {
    return (
      <AppShell back={{ to: '/app/tournaments', label: 'Tournaments' }}>
        <div className="t-loading"><div className="spinner" /></div>
      </AppShell>
    );
  }

  if (!event) {
    return (
      <AppShell back={{ to: '/app/tournaments', label: 'Tournaments' }}>
        <div className="t-empty">
          <div className="t-empty-icon"><AlertTriangle /></div>
          <div className="t-empty-title">Event not found</div>
          {error && <div className="t-empty-hint">{error}</div>}
        </div>
      </AppShell>
    );
  }

  const isOwner = event.myRole === 'owner';
  // Captains share management privileges with the owner for archive + delete.
  const canManage = isOwner || event.myRole === 'captain';
  const teamA = event.teams[0];
  const teamB = event.teams[1];
  const activePlayers = event.players.filter((p) => p.status !== 'removed');
  const dates = formatDateRange(event.startDate, event.endDate);
  const teamMap = new Map(event.teams.map((t) => [t.id, t]));

  return (
    <AppShell back={{ to: '/app/tournaments', label: 'Tournaments' }}>
      <section className="t-hero">
        <div className="t-hero-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
            <EventLogo logoUrl={event.logoUrl} name={event.name} size={64} />
            <div style={{ minWidth: 0 }}>
              <h1>{event.name}</h1>
              <p>
                {[event.locationName, dates].filter(Boolean).join('  ·  ') || 'No dates set'}
              </p>
            </div>
          </div>
          <StatusPill status={event.status} />
        </div>
      </section>

      {error && <div className="t-error">{error}</div>}

      {/* Scoreboard */}
      {teamA && teamB && (
        <div className="t-scoreboard">
          <div className="t-sb-team">
            <p className="t-sb-name" style={{ color: teamA.primaryColor ?? 'var(--text)' }}>{teamA.name}</p>
            <p className="t-sb-points">{formatTeamCupPoints(teamA.totalPoints)}</p>
          </div>
          <span className="t-sb-dash">—</span>
          <div className="t-sb-team">
            <p className="t-sb-name" style={{ color: teamB.primaryColor ?? 'var(--text)' }}>{teamB.name}</p>
            <p className="t-sb-points">{formatTeamCupPoints(teamB.totalPoints)}</p>
          </div>
        </div>
      )}

      <p className="t-hint" style={{ textAlign: 'center' }}>
        {event.pointsNeeded > 0
          ? `First to ${formatTeamCupPoints(event.pointsNeeded)} of ${formatTeamCupPoints(event.totalAvailablePoints)} available · ${winningRuleLabel(event.winningRuleType)}`
          : winningRuleLabel(event.winningRuleType)}
      </p>

      {/* Management actions — owner or captain. The backend already
          allows captains on Add Player / Edit / Teams; the UI now
          matches. Players don't see this row at all. */}
      {canManage && (
        <div className="t-actions">
          <Link to={`/app/tournaments/${event.id}/players/add`} className="t-btn t-btn-primary">
            <Plus /> Add Player
          </Link>
          <Link to={`/app/tournaments/${event.id}/teams`} className="t-btn t-btn-secondary">
            <Users /> Teams & Captains
          </Link>
          <Link to={`/app/tournaments/${event.id}/edit`} className="t-btn t-btn-ghost">
            <Pencil /> Edit
          </Link>
        </div>
      )}

      {/* My Matches — every player in the event sees their own matches
          here with a one-tap link to the score-entry page. Component
          renders nothing if the caller isn't in any matches yet. */}
      <MyMatchesSection eventId={event.id} />

      {/* Invite block — owner or captain only. Players shouldn't spread
          the invite code (per the roles spec). Backend also enforces
          owner-or-captain on add/invite endpoints, so even if a player
          got the code another way they couldn't add anyone with it. */}
      {event.inviteCode && (event.myRole === 'owner' || event.myRole === 'captain') && (
        <InviteBlock event={event} />
      )}

      {/* Team rosters — quick at-a-glance of who is on each team.
          We also compute team sizes so we can show a balance warning. The
          rule: a two-team match-play event needs equal-size teams.
          Round creation is blocked from RoundsList until this is true. */}
      {event.teams.length > 0 && (() => {
        const teamSizes = event.teams.map((t) => ({
          team: t,
          count: event.players.filter(
            (p) => p.teamId === t.id && p.status !== 'removed' && p.roleType !== 'owner',
          ).length,
        }));
        const allEqual = teamSizes.every((s) => s.count === teamSizes[0].count);
        return (
          <>
            <div className="t-section-label">Teams</div>
            {!allEqual && (
              <div
                className="t-error"
                style={{
                  borderColor: 'var(--gold)',
                  color: 'var(--gold)',
                  background: 'rgba(212, 175, 55, 0.08)',
                  marginBottom: 12,
                }}
              >
                <strong>Teams are unbalanced.</strong>{' '}
                {teamSizes.map((s, i) => (
                  <span key={s.team.id}>
                    {s.team.name} has {s.count}
                    {i < teamSizes.length - 1 ? ', ' : '. '}
                  </span>
                ))}
                Round creation is locked until both teams have the same number of players.
                Move someone between teams from the Teams & Captains page.
              </div>
            )}
            <div className="app-grid" style={{ marginTop: 0 }}>
              {event.teams.map((team) => {
                const members = event.players.filter(
                  (p) => p.teamId === team.id && p.status !== 'removed' && p.roleType !== 'owner',
                );
              const captain = members.find((p) => p.id === team.captainEventPlayerId);
              // Handicap order (plus handicaps first, no-handicap last),
              // captain pinned above via the separate render block.
              const others = members
                .filter((p) => p.id !== team.captainEventPlayerId)
                .sort((a, b) => {
                  const ha = a.eventHandicapIndex ?? Number.POSITIVE_INFINITY;
                  const hb = b.eventHandicapIndex ?? Number.POSITIVE_INFINITY;
                  if (ha !== hb) return ha - hb;
                  return (a.displayName ?? '').localeCompare(b.displayName ?? '', undefined, { sensitivity: 'base' });
                });
              return (
                <div
                  key={team.id}
                  className="t-section-card"
                  style={{ cursor: 'default', flexDirection: 'column', alignItems: 'stretch', gap: 12 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 12,
                        height: 12,
                        borderRadius: 6,
                        background: team.primaryColor ?? 'var(--text-mut)',
                      }}
                    />
                    <h3 className="t-section-card-title" style={{ margin: 0, flex: 1 }}>
                      {team.name}
                    </h3>
                    <span style={{ fontSize: 12, color: 'var(--text-sec)' }}>
                      {members.length} {members.length === 1 ? 'player' : 'players'}
                    </span>
                  </div>

                  {members.length === 0 ? (
                    <p className="t-hint" style={{ margin: 0 }}>
                      No players on this team yet. Use Add Player above.
                    </p>
                  ) : (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {captain && (
                        <li
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            color: 'var(--text)',
                            // Long captain names ellipsize instead of pushing
                            // the CAPTAIN tag + HCP into a ragged wrap on
                            // narrow phones.
                            minWidth: 0,
                          }}
                        >
                          <Star
                            width={14}
                            height={14}
                            fill="var(--gold)"
                            stroke="var(--gold)"
                            style={{ flexShrink: 0 }}
                          />
                          <span style={{ fontWeight: 700, minWidth: 60, flex: '0 1 auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{captain.displayName}</span>
                          <StatusDot player={captain} />
                          <span style={{ fontSize: 11, color: 'var(--gold)', fontWeight: 800, letterSpacing: 0.5 }}>
                            CAPTAIN
                          </span>
                          {captain.eventHandicapIndex != null && (
                            <span style={{ fontSize: 12, color: 'var(--text-mut)' }}>
                              · HCP {captain.eventHandicapIndex}
                            </span>
                          )}
                        </li>
                      )}
                      {others.map((p) => (
                        <li
                          key={p.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            color: 'var(--text)',
                            minWidth: 0,
                          }}
                        >
                          <span style={{ width: 14, display: 'inline-block', flexShrink: 0 }} />
                          <span style={{ minWidth: 60, flex: '0 1 auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.displayName}</span>
                          <StatusDot player={p} />
                          {p.eventHandicapIndex != null && (
                            <span style={{ fontSize: 12, color: 'var(--text-mut)' }}>
                              · HCP {p.eventHandicapIndex}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </>
        );
      })()}

      {/* Sub-sections (Rounds / Leaderboard / Draft / Recap) */}
      <div className="t-section-label">Sections</div>
      <div className="app-grid" style={{ marginTop: 0 }}>
        <Link to={`/app/tournaments/${event.id}/rounds`} className="t-section-card">
          <span className="t-section-card-icon t-section-card-icon-green"><Flag /></span>
          <span className="t-section-card-body">
            <h3 className="t-section-card-title">Rounds & Pairings</h3>
            <p className="t-section-card-sub">Build rounds with course, tee, and format. Create matches and assign players.</p>
          </span>
        </Link>
        <Link to={`/app/tournaments/${event.id}/leaderboard`} className="t-section-card">
          <span className="t-section-card-icon"><Trophy /></span>
          <span className="t-section-card-body">
            <h3 className="t-section-card-title">Leaderboard</h3>
            <p className="t-section-card-sub">Live points, per-round breakdown, and match cards — built for projecting on a TV.</p>
          </span>
        </Link>
        {canManage && (
          <Link to={`/app/tournaments/${event.id}/draft`} className="t-section-card">
            <span className="t-section-card-icon t-section-card-icon-green"><Shuffle /></span>
            <span className="t-section-card-body">
              <h3 className="t-section-card-title">Captain's Draft</h3>
              <p className="t-section-card-sub">Run a live snake draft on a real screen, or auto-balance teams by handicap.</p>
            </span>
          </Link>
        )}
        <Link to={`/app/tournaments/${event.id}/recap`} className="t-section-card">
          <span className="t-section-card-icon"><Newspaper /></span>
          <span className="t-section-card-body">
            <h3 className="t-section-card-title">Recap & Stats</h3>
            <p className="t-section-card-sub">Final result, MVP, top performers, and a one-tap share for the group chat.</p>
          </span>
        </Link>
      </div>

      {/* Overview */}
      <div className="t-section-label">Overview</div>
      <div>
        <div className="t-info-row">
          <span className="t-info-label">Format</span>
          <span className="t-info-value">Team Match Play</span>
        </div>
        <div className="t-info-row">
          <span className="t-info-label">Winning rule</span>
          <span className="t-info-value">{winningRuleLabel(event.winningRuleType)}</span>
        </div>
        <div className="t-info-row">
          <span className="t-info-label">Points / match</span>
          <span className="t-info-value">{formatTeamCupPoints(event.defaultPointsPerMatch)}</span>
        </div>
        {event.pointsNeededOverride != null && (
          <div className="t-info-row">
            <span className="t-info-label">Points to win (override)</span>
            <span className="t-info-value">{formatTeamCupPoints(event.pointsNeededOverride)}</span>
          </div>
        )}
        <div className="t-info-row">
          <span className="t-info-label">Visibility</span>
          <span className="t-info-value">{visibilityLabel(event.visibility)}</span>
        </div>
        <div className="t-info-row">
          <span className="t-info-label">Teams</span>
          <span className="t-info-value">{event.teams.length}</span>
        </div>
        <div className="t-info-row">
          <span className="t-info-label">Players</span>
          <span className="t-info-value">{activePlayers.length}</span>
        </div>
        {event.defendingTeamId && (
          <div className="t-info-row">
            <span className="t-info-label">Defending champ</span>
            <span className="t-info-value">{teamMap.get(event.defendingTeamId)?.name ?? '—'}</span>
          </div>
        )}
      </div>

      {/* Lifecycle actions. Publish + Mark Complete are captain-allowed
          (status changes are part of running the event). Archive and
          Delete stay owner-only per the roles spec Q2 guardrail. */}
      {canManage && event.status === 'draft' && (
        <button className="t-btn t-btn-primary t-btn-block" disabled={busy} onClick={() => patchStatus('active')} style={{ marginTop: 18 }}>
          <Send /> Publish Event
        </button>
      )}
      {canManage && event.status === 'active' && (
        <button className="t-btn t-btn-primary t-btn-block" disabled={busy} onClick={() => patchStatus('completed')} style={{ marginTop: 18 }}>
          <CheckCircle2 /> Mark Complete
        </button>
      )}
      {isOwner && event.status !== 'archived' && (
        <button
          type="button"
          className="t-btn t-btn-ghost t-btn-block"
          disabled={busy}
          onClick={() => setPendingConfirm('archive')}
          style={{ marginTop: 10 }}
        >
          Archive Event
        </button>
      )}
      {isOwner && (
        <button
          type="button"
          className="t-btn t-btn-danger-ghost t-btn-block"
          disabled={busy}
          onClick={() => setPendingConfirm('delete')}
          style={{ marginTop: 10 }}
        >
          Delete Event
        </button>
      )}

      {/* Reusable confirm modal — same component for both archive and delete.
          Delete requires typing "Delete Event" exactly to enable. */}
      <ConfirmModal
        open={pendingConfirm === 'archive'}
        title={`Archive "${event.name}"?`}
        body={(
          <>
            The event moves to the archived section. All players, rounds, and
            scores stay accessible but the event no longer shows up in your
            active tournaments list. You can restore it later.
          </>
        )}
        confirmLabel="Archive"
        tone="primary"
        busy={busy}
        onCancel={() => setPendingConfirm(null)}
        onConfirm={doArchive}
      />
      <ConfirmModal
        open={pendingConfirm === 'delete'}
        title={`Delete "${event.name}"?`}
        body={(
          <>
            This permanently removes the event, all teams, all players,
            all rounds, all matches, and all scores. <strong>This cannot be
            undone.</strong>
            <br />
            <br />
            If you might want this back later, archive it instead.
          </>
        )}
        requireType="Delete Event"
        confirmLabel="Delete forever"
        tone="danger"
        busy={busy}
        onCancel={() => setPendingConfirm(null)}
        onConfirm={doDelete}
      />
    </AppShell>
  );
}

// ── InviteBlock ───────────────────────────────────────────────────────────
// Big share card. Shows the join URL + code. Copy and SMS buttons.
// Visible to everyone (anyone in the event can invite their friends in).
function InviteBlock({ event }: { event: { name: string; inviteCode: string } }) {
  const [copied, setCopied] = useState(false);
  const joinUrl = `${window.location.origin}/app/join/${event.inviteCode}`;
  const smsBody = `You're invited to "${event.name}" on 18Gimmies. Tap to join: ${joinUrl}`;
  const smsHref = `sms:?&body=${encodeURIComponent(smsBody)}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Older browsers — fall back to a hidden textarea trick.
      const ta = document.createElement('textarea');
      ta.value = joinUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }

  return (
    <div
      style={{
        background: 'var(--card)',
        border: '1px solid var(--green-bright)',
        borderRadius: 12,
        padding: 16,
        marginTop: 14,
        marginBottom: 14,
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: 1.5,
          fontWeight: 800,
          color: 'var(--green-bright)',
          marginBottom: 8,
        }}
      >
        INVITE CODE
      </div>
      <div
        style={{
          fontSize: 28,
          fontWeight: 900,
          letterSpacing: 4,
          fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
          color: 'var(--text)',
          marginBottom: 10,
        }}
      >
        {event.inviteCode}
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--text-sec)',
          marginBottom: 12,
          wordBreak: 'break-all',
        }}
      >
        {joinUrl}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="t-btn t-btn-primary"
          onClick={copy}
          style={{ flex: 1, minWidth: 140 }}
        >
          {copied ? <Check /> : <Copy />}
          {copied ? 'Copied' : 'Copy link'}
        </button>
        <a
          href={smsHref}
          className="t-btn t-btn-secondary"
          style={{ flex: 1, minWidth: 140, textAlign: 'center' }}
        >
          <MessageSquare /> Text invite
        </a>
      </div>
    </div>
  );
}
